import * as React from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AdminCard } from './primitives'
import { cn } from '@/lib/utils'

export interface ResponsiveTableColumn<T> {
  key: string
  header: string
  render: (item: T) => React.ReactNode
  mobileLabel?: string
}

export interface ResponsiveTableProps<T> {
  columns: ResponsiveTableColumn<T>[]
  data: T[]
  emptyMessage?: string
  className?: string
}

export function ResponsiveTable<T extends Record<string, any>>({
  columns,
  data,
  emptyMessage = 'No data available',
  className,
}: ResponsiveTableProps<T>) {
  if (data.length === 0) {
    return (
      <AdminCard accentColor="info">
        <div className="text-center py-8 text-muted-foreground">
          {emptyMessage}
        </div>
      </AdminCard>
    )
  }

  return (
    <>
      {/* Desktop Table */}
      <div className={cn('hidden md:block', className)}>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key}>{column.header}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column.key}>{column.render(item)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className={cn('md:hidden space-y-3', className)}>
        {data.map((item, index) => (
          <AdminCard key={index} accentColor="info">
            <div className="space-y-3">
              {columns.map((column) => (
                <div key={column.key} className="flex flex-col gap-1">
                  <div className="text-xs font-medium text-muted-foreground">
                    {column.mobileLabel || column.header}
                  </div>
                  <div className="text-sm">{column.render(item)}</div>
                </div>
              ))}
            </div>
          </AdminCard>
        ))}
      </div>
    </>
  )
}

